import React from 'react';
import { OpenImgContextProvider } from 'openimg/react';
import {
  data,
  Link,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useMatches,
  useRouteError,
  isRouteErrorResponse,
} from 'react-router';
import { HoneypotProvider } from 'remix-utils/honeypot/react';
import { type Route } from './+types/root.ts';
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png';
import faviconAssetUrl from './assets/favicons/favicon.svg';
import { CarbonShell } from './components/carbon-shell.tsx';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { NotFound } from './components/not-found.tsx';
import { GlobalProgress } from './components/progress-bar.tsx';
import { SearchBar } from './components/search-bar.tsx';
import { useToast } from './components/toaster.tsx';
import { Button } from './components/ui/button.tsx';
import { href as iconsHref } from './components/ui/icon.tsx';
// import { EpicToaster } from './components/ui/sonner.tsx';
// import { UserDropdown } from './components/user-dropdown.tsx';
// import {
//   ThemeSwitch,
//   useOptionalTheme,
//   useTheme,
// } from './routes/resources/theme-switch.tsx';
import tailwindStyleSheetUrl from './styles/tailwind.css?url';
import { getUserId, logout } from './utils/auth.server.ts';
import { ClientHintCheck, getHints } from './utils/client-hints.tsx';
import { prisma } from './utils/db.server.ts';
import { getEnv } from './utils/env.server.ts';
import { pipeHeaders } from './utils/headers.server.ts';
import { honeypot } from './utils/honeypot.server.ts';
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx';
import { useNonce } from './utils/nonce-provider.ts';
import { type Theme, getTheme } from './utils/theme.server.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';
import { useOptionalUser } from './utils/user.ts';
import { getSystemName, isSystemOnline } from './utils/system-name.server.ts';
import { userHasRole } from './utils/permissions.server.ts';
import {
  AnimatedNotification,
  useAnimatedNotification,
} from './components/animated-notification.tsx';
import { NotifyProvider } from './components/notify.tsx';
import { IdleWarningModal } from './components/idle-warning-modal.tsx';
import { useSimpleIdleTimeout } from './hooks/useSimpleIdleTimeout.ts';
import { TailwindIndicator } from '#app/components/tw-indicator.tsx';

export const links: Route.LinksFunction = () => {
  return [
    // Preload svg sprite as a resource to avoid render blocking
    { rel: 'preload', href: iconsHref, as: 'image' },
    {
      rel: 'icon',
      href: '/favicon.ico',
      sizes: '48x48',
    },
    { rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
    { rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
    {
      rel: 'manifest',
      href: '/site.webmanifest',
      crossOrigin: 'use-credentials',
    } as const, // necessary to make typescript happy
    { rel: 'stylesheet', href: tailwindStyleSheetUrl },
  ].filter(Boolean);
};

export const meta: Route.MetaFunction = ({ data }) => {
  const name = data?.systemName ?? 'SwellFox Waves';
  return [
    { title: data ? name : `Error | ${name}` },
    {
      name: 'description',
      content: `A comprehensive knowledge base and collaboration platform for managing documentation, projects, and team workflows`,
    },
  ];
};

export async function loader({ request }: Route.LoaderArgs) {
  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root',
  });

  const user = userId
    ? await time(
        () =>
          prisma.user.findUnique({
            select: {
              id: true,
              name: true,
              username: true,
              image: { select: { objectKey: true } },
              roles: {
                select: {
                  name: true,
                  permissions: {
                    select: { entity: true, action: true, access: true },
                  },
                },
              },
            },
            where: { id: userId },
          }),
        { timings, type: 'find user', desc: 'find user in root' },
      )
    : null;
  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: '/' });
  }

  // Check if system is online and handle maintenance mode
  const systemOnline = await isSystemOnline();
  const isAdmin = userId ? await userHasRole(userId, 'admin') : false;

  // Define routes that should be accessible during maintenance mode
  const maintenanceAllowedRoutes = [
    '/under-construction',
    '/login',
    '/logout',
    '/auth',
    '/nexus',
    '/verify',
    '/onboarding',
    '/forgot-password',
    '/reset-password',
    '/resources',
    '/healthcheck',
  ];

  const url = new URL(request.url);
  const isAllowedRoute = maintenanceAllowedRoutes.some((route) =>
    url.pathname.startsWith(route),
  );

  // If system is offline and user is not admin, redirect to maintenance page
  // Allow access to authentication routes so admins can log in
  if (!systemOnline && !isAdmin && !isAllowedRoute) {
    throw redirect('/under-construction');
  }

  const { toast, headers: toastHeaders } = await getToast(request);
  const honeyProps = await honeypot.getInputProps();
  const systemName = await getSystemName();

  return data(
    {
      user,
      systemName,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request),
        },
      },
      ENV: getEnv(),
      toast,
      honeyProps,
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders,
      ),
    },
  );
}

export const headers: Route.HeadersFunction = pipeHeaders;

function Document({
  children,
  nonce,
  theme = 'light',
  env = {},
}: {
  children: React.ReactNode;
  nonce: string;
  theme?: Theme;
  env?: Record<string, string | undefined>;
}) {
  const allowIndexing = ENV.ALLOW_INDEXING !== 'false';
  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        {allowIndexing ? null : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <Links />
      </head>
      <body className="bg-background text-foreground">
        {children}
        <script
          nonce={nonce}
          // suppressHydrationWarning is required for CSP nonces
          // Nonces must be unique per request for security, so they will always
          // differ between server render and client hydration. This is expected
          // behavior and doesn't affect functionality.
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
        <TailwindIndicator />
      </body>
    </html>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  // if there was an error running the loader, data could be missing
  const data = useLoaderData<typeof loader | null>();
  const nonce = useNonce();
  // Use actual theme from loader, fallback to system preference from hints
  const theme =
    data?.requestInfo.userPrefs.theme ?? data?.requestInfo.hints.theme;
  return (
    <Document nonce={nonce} theme={theme} env={data?.ENV}>
      {children}
    </Document>
  );
}

function App() {
  const data = useLoaderData<typeof loader>();
  const {
    notification,
    hideNotification,
    showError,
    showSuccess,
    showWarning,
    showInfo,
  } = useAnimatedNotification();

  // Simple idle timeout - only checks every 5 minutes, no persistent connections
  const idleTimeout = useSimpleIdleTimeout(data.user);

  // Handle toast from loader data
  React.useEffect(() => {
    if (data.toast) {
      if (data.toast.type === 'success') {
        showSuccess(data.toast.title || 'Success', data.toast.description);
      } else if (data.toast.type === 'error') {
        showError(data.toast.title || 'Error', data.toast.description);
      } else {
        showSuccess(data.toast.title || 'Notification', data.toast.description);
      }
    }
  }, [data.toast, showSuccess, showError]);

  const handleLogoutNow = () => {
    // For idle timeout, redirect to login with session expired message and preserve URL
    const currentPath = window.location.pathname + window.location.search;
    const redirectUrl =
      currentPath !== '/'
        ? `/login?redirectTo=${encodeURIComponent(currentPath)}&message=session-expired`
        : '/login?message=session-expired';
    window.location.href = redirectUrl;
  };

  const user = useOptionalUser();

  // Use actual theme from loader, fallback to system preference from hints
  const theme =
    data.requestInfo.userPrefs.theme ?? data.requestInfo.hints.theme;
  const matches = useMatches();
  const isOnSearchPage = matches.find((m) => m.id === 'routes/users/index');
  const searchBar = isOnSearchPage ? null : <SearchBar status="idle" />;
  useToast(data.toast);

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <OpenImgContextProvider
        optimizerEndpoint="/resources/images"
        getSrc={getImgSrc}
      >
        <NotifyProvider
          showSuccess={showSuccess}
          showError={showError}
          showInfo={showInfo}
          showWarning={showWarning}
        >
          <Outlet />
          {/* Idle timeout warning modal */}
          <IdleWarningModal
            isOpen={idleTimeout.showWarning}
            minutesLeft={idleTimeout.minutesLeft}
            secondsLeft={idleTimeout.secondsLeft}
            onExtendSession={idleTimeout.extendSession}
            onLogoutNow={handleLogoutNow}
          />

          <AnimatedNotification
            show={notification.show}
            kind={notification.kind}
            title={notification.title}
            messages={notification.messages}
            onClose={hideNotification}
            autoHideDelay={notification.config?.autoHideDelay}
            position={notification.config?.position}
            width={notification.config?.width}
            duration={notification.config?.duration}
            offsetFromEdge={notification.config?.offsetFromEdge}
          />
          <GlobalProgress />
        </NotifyProvider>
      </OpenImgContextProvider>
    </div>
  );
}

function Logo() {
  return (
    <Link to="/" className="group grid leading-snug">
      <span className="font-light transition group-hover:-translate-x-1">
        epic
      </span>
      <span className="font-bold transition group-hover:translate-x-1">
        stack
      </span>
    </Link>
  );
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <HoneypotProvider {...data.honeyProps}>
      <App />
    </HoneypotProvider>
  );
}

export default AppWithProviders;

// Custom error boundary with 404 handler
export function ErrorBoundary() {
  const error = useRouteError();

  // Handle 404 errors with custom NotFound component
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }

  // For all other errors, use the general error boundary
  return <GeneralErrorBoundary />;
}
